import React, { useCallback, useEffect, useState } from 'react';
import { authFetch } from '../../utils/api';

// Admin-only "Replay (Backtest)" tab — engine-authoritative OSUPA reconstruction.
// Read-only view of the isolated osupa_decision_replay table. Nothing here writes.
export default function ReplayPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await authFetch('/strategies/api/admin/replay/osupa/');
      const j = await res.json();
      if (!j.ok) throw new Error(j.error || 'replay unavailable');
      setData(j);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [dl, setDl] = useState(false);
  const download = useCallback(async () => {
    setDl(true);
    try {
      const res = await authFetch('/strategies/api/admin/replay/osupa/export/');
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'OSUPA_Engine_Replay.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed: ' + e.message); }
    finally { setDl(false); }
  }, []);

  if (loading) return <div style={{ padding: 24, color: '#8b93a7' }}>Loading OSUPA replay…</div>;
  if (err) return <div style={{ padding: 24, color: '#f87171' }}>Replay: {err}</div>;
  if (!data) return null;

  const s = data.summary, r = data.reconciliation;
  const rows = data.rows.filter(x => filter === 'ALL' ? true : filter === 'TAKE' ? x.action === 'TAKE' : x.action === 'SKIP');
  const card = (n, v, c) => (
    <div style={{ background: '#151b30', border: '1px solid #232b45', borderRadius: 10, padding: '12px 16px', minWidth: 130 }}>
      <div style={{ color: '#8b93a7', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{n}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: c || '#e6e9ef' }}>{v}</div>
    </div>
  );

  return (
    <div style={{ padding: '4px 2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>OSUPA Engine-Replay</h3>
        <span style={{ background: '#14331f', color: '#34d399', border: '1px solid #1f6b3f', borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>
          ENGINE-AUTHORITATIVE · ADMIN ONLY
        </span>
      </div>
      <div style={{ color: '#8b93a7', fontSize: 12, marginBottom: 14 }}>
        Real OSUPA grade engine walked over {s.source} · {s.span[0]} → {s.span[1]} · not a loaded book
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        {card('Sessions', s.sessions)}
        {card('HOLD nights', s.hold_nights)}
        {card('TAKE', s.take, '#34d399')}
        {card('Tier A / B / C', `${s.tier.A} / ${s.tier.B} / ${s.tier.C}`)}
        {card('Skip: gate / C', `${s.skip_gate} / ${s.skip_c}`)}
        {card('Take-agree vs book', `${r.take_agree_pct}%`, r.take_agree_pct >= 90 ? '#34d399' : '#e09b00')}
      </div>

      <div style={{ background: '#111731', border: '1px solid #232b45', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: '#c7ccda' }}>
        <b style={{ color: '#8ab4f8' }}>Reconciliation vs 06-27 book</b> — both {r.both}/{r.book_take} takes ·
        book-only {r.book_only.total} (gate_skip {r.book_only.gate_skip}, C {r.book_only.composite_C}, tail {r.book_only.tail_gap}) ·
        replay-only {r.replay_only.total} (800bp-override refinement {r.replay_only.override_800bp_refinement}, other {r.replay_only.other})
        <div style={{ color: '#8b93a7', marginTop: 6 }}>{r.note}</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {['ALL', 'TAKE', 'SKIP'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ background: filter === f ? '#2962ff' : '#1b2238', color: '#e6e9ef', border: '1px solid #2b3557',
                     borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}>{f}</button>
        ))}
        <button onClick={download} disabled={dl} style={{ marginLeft: 'auto', background: '#14331f', color: '#34d399', border: '1px solid #1f6b3f', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: dl ? 'wait' : 'pointer' }}>{dl ? 'Preparing…' : '⬇ Download .xlsx'}</button>
        <button onClick={load} style={{ background: '#1b2238', color: '#8ab4f8', border: '1px solid #2b3557', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>

      <div style={{ maxHeight: 460, overflow: 'auto', border: '1px solid #232b45', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr>
            {['Date', 'Decision', 'Action', 'Tier', 'Score', 'Entry(16:00)', 'ret1 bp', 'ret5 bp', 'ext bp', 'Reason'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '5px 8px', position: 'sticky', top: 0, background: '#0b1020', color: '#8b93a7', borderBottom: '1px solid #232b45' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((x, i) => (
              <tr key={i} style={{ opacity: x.action === 'TAKE' ? 1 : 0.62 }}>
                <td style={td}>{x.session_date}</td>
                <td style={td}>{x.decision}</td>
                <td style={{ ...td, color: x.action === 'TAKE' ? '#34d399' : '#8b93a7', fontWeight: x.action === 'TAKE' ? 600 : 400 }}>{x.action}</td>
                <td style={td}>{x.grade_tier || ''}</td>
                <td style={td}>{x.grade_score != null ? Number(x.grade_score).toFixed(3) : ''}</td>
                <td style={td}>{x.entry_price != null ? Number(x.entry_price).toFixed(2) : ''}</td>
                <td style={td}>{x.ret1_bp != null ? Number(x.ret1_bp).toFixed(1) : ''}</td>
                <td style={td}>{x.ret5_bp != null ? Number(x.ret5_bp).toFixed(1) : ''}</td>
                <td style={td}>{x.ext_bp != null ? Number(x.ext_bp).toFixed(1) : ''}</td>
                <td style={{ ...td, color: '#8b93a7' }}>{x.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ color: '#8b93a7', fontSize: 11, marginTop: 8 }}>{rows.length} rows shown · engine code is private (not deployed) — this is a read-only view of the isolated replay table.</div>
    </div>
  );
}
const td = { padding: '4px 8px', borderBottom: '1px solid #1b2238' };
